/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useQuery } from '@tanstack/react-query';

import { ApiError, GetUserProfileResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';

/**
 * Custom hook that fetches a user profile
 *
 * Features:
 * - Fetches a user profile
 * - Type-safe API response handling
 *
 * @returns React Query result containing user profile info
 *
 * @example
 * ```tsx
 * const { data: userProfile } = useUserProfileQuery();
 * ```
 */
type UseUserProfileQueryOptions = {
  retry?: boolean;
  refetchOnWindowFocus?: boolean;
};

export function useUserProfileQuery(options: UseUserProfileQueryOptions = {}) {
  const { retry = false, refetchOnWindowFocus = true } = options;
  return useQuery<GetUserProfileResponse, ApiError>({
    queryKey: ['userProfile'],
    queryFn: async () => {
      const profileResponse = await mgmtPlaneAPIClient.users.getUserProfile();
      return profileResponse;
    },
    retry,
    refetchOnWindowFocus,
  });
}
